import { useTranslation } from 'react-i18next';
import {
  getRbdDragEndMessage,
  getRbdDragHandleInstructions,
  getRbdDragStartMessage,
  getRbdDragUpdateMessage,
} from 'core/utils/dragAndDropScreenReader';
import type { RbdDragStart, RbdDragUpdate, RbdDropResult, RbdResponderProvided } from 'core/utils/dragAndDropScreenReader';

type UseDraggableItemsListParams = {
  disableDrag?: boolean;
  disableEditMode?: boolean;
  dragHandleAriaLabel?: string;
  handleOnDragEnd?: (result: RbdDropResult) => void;
};

export type UseDraggableItemsListReturn = {
  dragHandleLabel: string;
  dragHandleUsageInstructions: string;
  isDragDisabled: boolean;
  onDragEnd: (result: RbdDropResult, provided: RbdResponderProvided) => void;
  onDragStart: (start: RbdDragStart, provided: RbdResponderProvided) => void;
  onDragUpdate: (update: RbdDragUpdate, provided: RbdResponderProvided) => void;
};

function useDraggableItemsList({
  disableDrag = false,
  disableEditMode = true,
  dragHandleAriaLabel,
  handleOnDragEnd,
}: UseDraggableItemsListParams): UseDraggableItemsListReturn {
  const { t } = useTranslation();

  const isDragDisabled = disableEditMode || disableDrag;

  // Escape hatch de contexto (p. ej. "reordenar módulos" vs "reordenar
  // contenidos"); por defecto usa la clave genérica del DS.
  const dragHandleLabel = dragHandleAriaLabel ?? t('common:DragToReorder');

  // Con `announce` react-beautiful-dnd no lee su anuncio por defecto, que está en inglés (TAN-10927).
  function onDragStart(start: RbdDragStart, provided: RbdResponderProvided): void {
    provided.announce(getRbdDragStartMessage(t, start));
  }

  function onDragUpdate(update: RbdDragUpdate, provided: RbdResponderProvided): void {
    provided.announce(getRbdDragUpdateMessage(t, update));
  }

  function onDragEnd(result: RbdDropResult, provided: RbdResponderProvided): void {
    provided.announce(getRbdDragEndMessage(t, result));
    handleOnDragEnd?.(result);
  }

  return {
    dragHandleLabel,
    dragHandleUsageInstructions: getRbdDragHandleInstructions(t),
    isDragDisabled,
    onDragEnd,
    onDragStart,
    onDragUpdate,
  };
}

export default useDraggableItemsList;
